import type { AccessTokenProvider } from "~application/auth/ports/access-token-provider.ts";
import type { AccessToken, RefreshToken, Tokens } from "~domain/auth/models/authentication-model.ts";

import { ELocalStorageKeys } from "~application/constants/local-storage.constant.ts";
import { type Either, Err, Ok } from "~utils/either.ts";

export class LocalStorageAccessTokenProvider implements AccessTokenProvider {
  async get(): Promise<Either<Tokens, null>> {
    const [accessToken, refreshToken] = await Promise.all([this.getAccessToken(), this.getRefreshToken()]);

    if (accessToken.success && refreshToken.success) {
      return Ok({
        accessToken: accessToken.value,
        refreshToken: refreshToken.value,
      });
    }

    return Err(null);
  }

  async getAccessToken(): Promise<Either<AccessToken, null>> {
    const maybeAccessToken = localStorage.getItem(ELocalStorageKeys.ACCESS_TOKEN) as AccessToken | null;
    if (maybeAccessToken) {
      return { success: true, value: maybeAccessToken };
    }

    return { success: false, error: null };
  }

  async clear(): Promise<void> {
    localStorage.removeItem(ELocalStorageKeys.ACCESS_TOKEN);
    localStorage.removeItem(ELocalStorageKeys.REFRESH_TOKEN);
  }

  async getRefreshToken(): Promise<Either<RefreshToken, null>> {
    const maybeRefreshToken = localStorage.getItem(ELocalStorageKeys.REFRESH_TOKEN) as RefreshToken | null;
    if (maybeRefreshToken) {
      return { success: true, value: maybeRefreshToken };
    }

    return { success: false, error: null };
  }

  async saveAccessToken(accessToken: AccessToken): Promise<void> {
    localStorage.setItem(ELocalStorageKeys.ACCESS_TOKEN, accessToken);
  }

  async saveRefreshToken(refreshToken: RefreshToken): Promise<void> {
    localStorage.setItem(ELocalStorageKeys.REFRESH_TOKEN, refreshToken);
  }
}
